import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  alpha,
  keyframes,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PeopleIcon from '@mui/icons-material/People';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TableChartIcon from '@mui/icons-material/TableChart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GavelIcon from '@mui/icons-material/Gavel';
import { analyticsService } from '../services/analytics.service';
import { tradeService } from '../services/trade.service';
import { clientService } from '../services/client.service';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { DashboardSummary, Client, TradeType, ClientSummary } from '../types';
import { formatIndianNumber, formatCurrency } from '../utils/formatters';
import BadgeIcon from '@mui/icons-material/Badge';

// Components
import ClientSummaryTable from '../components/dashboard/ClientSummaryTable';

// Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

// Premium color palette - Pink/Purple theme
const COLORS = {
  // Primary pink
  primary: '#EC4899',
  primaryLight: '#F472B6',
  primaryDark: '#DB2777',
  primaryGlow: 'rgba(236, 72, 153, 0.4)',

  // Accent purple
  secondary: '#8B5CF6',
  secondaryLight: '#A78BFA',
  secondaryDark: '#7C3AED',

  // Purple accents
  purple: '#8B5CF6',
  purpleLight: '#A78BFA',
  purpleDark: '#7C3AED',

  // Status colors (keeping green/red for P&L semantic meaning)
  positive: '#10B981',
  negative: '#EF4444',
  warning: '#F59E0B',
  info: '#06B6D4',

  // Text colors
  text: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#94A3B8',

  // Backgrounds
  cardBg: '#FFFFFF',
  pageBg: '#FAF5FF',
  glassBg: 'rgba(255, 255, 255, 0.7)',
  border: '#E2E8F0',

  // Gradients
  heroGradient: 'linear-gradient(135deg, #831843 0%, #6B21A8 50%, #4C1D95 100%)',
  pinkGradient: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
  greenGradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
  blueGradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
  purpleGradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
  orangeGradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
  redGradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',

  // Glass effects
  glassBorder: 'rgba(255, 255, 255, 0.18)',
  glassBlur: 'blur(20px)',
};

interface ImportRow {
  clientId: string;
  tradeType: string;
  date: string;
  scrip: string;
  purchaseLots: number;
  lotSize: number;
  purchasePrice: number;
  saleLots?: number;
  salePrice?: number;
  currentPrice?: number;
}

// Stat Card Component
interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  gradient: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  delay?: number;
  onClick?: () => void;
  action?: React.ReactNode;
}

function StatCard({ title, value, subtitle, icon, gradient, trend, trendValue, delay = 0, onClick, action }: StatCardProps) {
  return (
    <Card
      onClick={onClick}
      sx={{
        background: gradient,
        color: 'white',
        borderRadius: '20px',
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        animation: `${float} 6s ease-in-out infinite`,
        animationDelay: `${delay}s`,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)',
          pointerEvents: 'none',
        },
      }}
    >
      <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        {/* Decorative circles */}
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.1)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            right: 40,
            width: 60,
            height: 60,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.08)',
          }}
        />

        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: '14px',
              bgcolor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {action}
            {trend && trendValue && (
              <Chip
                icon={trend === 'up' ? <ArrowUpwardIcon sx={{ fontSize: 14 }} /> : <ArrowDownwardIcon sx={{ fontSize: 14 }} />}
                label={trendValue}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  backdropFilter: 'blur(10px)',
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
            )}
          </Box>
        </Box>

        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500, mb: 0.5, letterSpacing: '0.5px' }}>
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: '2rem',
            fontWeight: 800,
            lineHeight: 1.2,
            textShadow: '0 2px 10px rgba(0,0,0,0.2)',
            color: 'white',
          }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1, display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// Section Header Component
interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}

function SectionHeader({ icon, title, subtitle, action }: SectionHeaderProps) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <Box display="flex" alignItems="center" gap={2}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: '12px',
            bgcolor: alpha(COLORS.primary, 0.1),
            color: COLORS.primary,
            display: 'flex',
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} color={COLORS.text}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color={COLORS.textMuted}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {action}
    </Box>
  );
}

// Dealer Revenue Popup Component
interface DealerRevenuePopupProps {
  open: boolean;
  onClose: () => void;
  clientSummaries: ClientSummary[];
}

function DealerRevenuePopup({ open, onClose, clientSummaries }: DealerRevenuePopupProps) {
  // Group by referrer/agent - since we don't have agent field directly,
  // we show per-client revenue breakdown sorted by revenue
  const sortedByRevenue = useMemo(() => {
    return [...clientSummaries].sort((a, b) => b.totalBrokerage - a.totalBrokerage);
  }, [clientSummaries]);

  const totalRevenue = useMemo(() => {
    return sortedByRevenue.reduce((sum, c) => sum + c.totalBrokerage, 0);
  }, [sortedByRevenue]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '24px' },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: '12px',
                bgcolor: alpha(COLORS.primary, 0.1),
                display: 'flex',
              }}
            >
              <MonetizationOnIcon sx={{ color: COLORS.primary }} />
            </Box>
            <Typography variant="h5" fontWeight={700}>
              Dealer Revenue Breakdown
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TableContainer component={Paper} sx={{ borderRadius: '14px', boxShadow: 'none', border: `1px solid ${COLORS.border}` }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                <TableCell sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  Client Name
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  Open Positions
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  Turnover
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                  Revenue Generated
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedByRevenue.map((client) => (
                <TableRow key={client.clientId} sx={{ '&:hover': { bgcolor: alpha(COLORS.primary, 0.04) } }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color={COLORS.text}>
                      {client.clientName}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={COLORS.textSecondary}>
                      {client.openPositions}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color={COLORS.textSecondary}>
                      {formatCurrency(client.totalTurnover)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700} color={COLORS.positive}>
                      {formatCurrency(client.totalBrokerage)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {/* Total row */}
              <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                <TableCell colSpan={3}>
                  <Typography variant="body2" fontWeight={700} color={COLORS.text}>
                    Total
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body1" fontWeight={800} color={COLORS.primary}>
                    {formatCurrency(totalRevenue)}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={onClose}
          sx={{
            color: COLORS.textMuted,
            fontWeight: 600,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardSummary | null>(null);

  // Check if trading features are disabled for current user's role (HR is always available)
  const tradingFeaturesDisabled = !isSuperAdmin && !isFeatureEnabled('dashboard');

  // Period selector state
  const [selectedPeriod, setSelectedPeriod] = useState<'D' | 'W' | 'M' | 'Y'>('D');

  // Dealer Revenue Popup state
  const [dealerRevenueOpen, setDealerRevenueOpen] = useState(false);

  // Import dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importData, setImportData] = useState<ImportRow[]>([]);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [, setClients] = useState<Client[]>([]);
  const [importResult, setImportResult] = useState<{ success: number; failed: number } | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const data = await analyticsService.getDashboardSummary();
        setDashboardData(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const fetchClients = async () => {
    try {
      const data = await clientService.getClients();
      setClients(data);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  };

  const handleOpenImportDialog = () => {
    fetchClients();
    setImportDialogOpen(true);
    setImportData([]);
    setImportErrors([]);
    setImportResult(null);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter((line) => line.trim());
        const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());

        const requiredHeaders = ['clientid', 'tradetype', 'date', 'scrip', 'purchaselots', 'lotsize', 'purchaseprice'];
        const missingHeaders = requiredHeaders.filter((h) => !headers.includes(h));

        if (missingHeaders.length > 0) {
          setImportErrors([`Missing required columns: ${missingHeaders.join(', ')}`]);
          return;
        }

        const rows: ImportRow[] = [];
        const errors: string[] = [];

        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map((v) => v.trim());
          if (values.length < headers.length) continue;

          const row: Record<string, string> = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });

          if (!row.clientid || !row.tradetype || !row.date || !row.scrip) {
            errors.push(`Row ${i}: Missing required fields`);
            continue;
          }

          rows.push({
            clientId: row.clientid,
            tradeType: row.tradetype,
            date: row.date,
            scrip: row.scrip.toUpperCase(),
            purchaseLots: parseFloat(row.purchaselots) || 0,
            lotSize: parseFloat(row.lotsize) || 1,
            purchasePrice: parseFloat(row.purchaseprice) || 0,
            saleLots: row.salelots ? parseFloat(row.salelots) : undefined,
            salePrice: row.saleprice ? parseFloat(row.saleprice) : undefined,
            currentPrice: row.currentprice ? parseFloat(row.currentprice) : undefined,
          });
        }

        setImportData(rows);
        setImportErrors(errors);
      } catch (err) {
        setImportErrors(['Failed to parse CSV file. Please check the format.']);
      }
    };

    reader.readAsText(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImport = async () => {
    if (importData.length === 0) {
      setError('No data to import');
      return;
    }

    try {
      setImporting(true);

      const trades = importData.map((row) => ({
        clientId: row.clientId,
        tradeType: row.tradeType as TradeType,
        date: row.date,
        scrip: row.scrip,
        purchaseLots: row.purchaseLots,
        lotSize: row.lotSize,
        purchasePrice: row.purchasePrice,
        saleLots: row.saleLots,
        salePrice: row.salePrice,
        currentPrice: row.currentPrice,
      }));

      const result = await tradeService.bulkImport(trades);
      setImportResult(result.results);

      if (result.results.success > 0) {
        setSuccess(`Successfully imported ${result.results.success} trades`);
        const data = await analyticsService.getDashboardSummary();
        setDashboardData(data);
      }

      if (result.results.failed > 0) {
        setImportErrors(result.results.errors.map((e: any) => `Row ${e.index + 1}: ${e.error}`));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to import trades');
    } finally {
      setImporting(false);
    }
  };

  // Compute total trades count
  const totalTrades = useMemo(() => {
    if (!dashboardData?.tradesByType) return 0;
    const t = dashboardData.tradesByType;
    return (t.equity || 0) + (t.future || 0) + (t.option || 0);
  }, [dashboardData]);

  // Compute open trades count
  const totalOpenTrades = useMemo(() => {
    if (!dashboardData?.openTradesByType) return 0;
    const t = dashboardData.openTradesByType;
    return (t.equity || 0) + (t.future || 0) + (t.option || 0);
  }, [dashboardData]);

  // Loading skeleton
  if (loading) {
    return (
      <Box>
        <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
          <Skeleton width={200} height={24} sx={{ borderRadius: '4px' }} />
          <Box display="flex" gap={2}>
            <Skeleton width={120} height={40} sx={{ borderRadius: '12px' }} variant="rectangular" />
            <Skeleton width={120} height={40} sx={{ borderRadius: '12px' }} variant="rectangular" />
          </Box>
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} lg={3} key={i}>
              <Skeleton height={180} sx={{ borderRadius: '20px' }} variant="rectangular" />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Skeleton height={60} sx={{ borderRadius: '12px' }} variant="rectangular" />
          </Grid>
          <Grid item xs={12}>
            <Skeleton height={400} sx={{ borderRadius: '20px' }} variant="rectangular" />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* HR Module Available Banner - shown when trading features are disabled */}
      {tradingFeaturesDisabled && (
        <Alert
          severity="info"
          icon={<BadgeIcon />}
          sx={{
            mb: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)',
            border: '1px solid #10B981',
            '& .MuiAlert-icon': {
              color: '#059669',
            },
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate('/employees')}
              sx={{
                fontWeight: 600,
                color: '#047857',
                '&:hover': {
                  bgcolor: 'rgba(16, 185, 129, 0.1)',
                },
              }}
            >
              Go to HR Module
            </Button>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#065F46' }}>
            HR Module is fully operational! Manage employees, track attendance, and generate salary slips.
          </Typography>
        </Alert>
      )}

      {/* ===== COMPACT TOP BAR ===== */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        sx={{
          p: 2,
          borderRadius: '16px',
          bgcolor: COLORS.cardBg,
          border: `1px solid ${COLORS.border}`,
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.04)',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <AccessTimeIcon sx={{ fontSize: 18, color: COLORS.textMuted }} />
          <Typography variant="body2" sx={{ color: COLORS.textSecondary, fontWeight: 500 }}>
            {new Date().toLocaleDateString('en-IN', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/trades/new')}
            sx={{
              background: COLORS.pinkGradient,
              borderRadius: '12px',
              px: 3,
              py: 1,
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: '0 8px 25px rgba(236, 72, 153, 0.4)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 35px rgba(236, 72, 153, 0.5)',
              },
            }}
          >
            New Trade
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={handleOpenImportDialog}
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none',
              borderColor: COLORS.border,
              color: COLORS.textSecondary,
              '&:hover': {
                borderColor: COLORS.primary,
                color: COLORS.primary,
                bgcolor: alpha(COLORS.primary, 0.05),
              },
            }}
          >
            Import CSV
          </Button>
        </Box>
      </Box>

      {/* ===== STAT CARDS ROW ===== */}
      <Grid container spacing={3} mb={3}>
        {/* Revenue Card */}
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Revenue"
            value={formatIndianNumber(dashboardData?.totalRevenue || 0)}
            subtitle="Total brokerage earned"
            icon={<MonetizationOnIcon sx={{ fontSize: 24, color: 'white' }} />}
            gradient={COLORS.pinkGradient}
            delay={0}
            action={
              <Tooltip title="View dealer revenue breakdown">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDealerRevenueOpen(true);
                  }}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                    width: 28,
                    height: 28,
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            }
          />
        </Grid>

        {/* Total Turnover Card */}
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Total Turnover"
            value={formatIndianNumber(dashboardData?.totalTurnover || 0)}
            subtitle={`Option Lots: ${dashboardData?.totalOptionLots || 0}`}
            icon={<AccountBalanceWalletIcon sx={{ fontSize: 24, color: 'white' }} />}
            gradient={COLORS.purpleGradient}
            delay={0.1}
          />
        </Grid>

        {/* Trades Card */}
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Trades"
            value={totalTrades}
            subtitle={`E: ${dashboardData?.tradesByType?.equity || 0} | F: ${dashboardData?.tradesByType?.future || 0} | O: ${dashboardData?.tradesByType?.option || 0}`}
            icon={<ShowChartIcon sx={{ fontSize: 24, color: 'white' }} />}
            gradient={COLORS.blueGradient}
            trend={totalOpenTrades > 0 ? 'up' : undefined}
            trendValue={totalOpenTrades > 0 ? `${totalOpenTrades} open` : undefined}
            delay={0.2}
            onClick={() => navigate('/trades')}
          />
        </Grid>

        {/* Active Clients Card */}
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard
            title="Active Clients"
            value={`${dashboardData?.activeClients || 0} / ${dashboardData?.totalClients || 0}`}
            subtitle="Active / Total clients"
            icon={<PeopleIcon sx={{ fontSize: 24, color: 'white' }} />}
            gradient={COLORS.greenGradient}
            delay={0.3}
            onClick={() => navigate('/clients')}
          />
        </Grid>
      </Grid>

      {/* ===== PERIOD SELECTOR ===== */}
      <Box
        mb={4}
        display="flex"
        alignItems="center"
        gap={2}
        sx={{
          p: 1.5,
          borderRadius: '14px',
          bgcolor: COLORS.cardBg,
          border: `1px solid ${COLORS.border}`,
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.04)',
        }}
      >
        <Typography variant="body2" fontWeight={600} color={COLORS.textSecondary} sx={{ ml: 1 }}>
          Period:
        </Typography>
        <ToggleButtonGroup
          value={selectedPeriod}
          exclusive
          onChange={(_e, val) => val && setSelectedPeriod(val)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              borderRadius: '10px !important',
              px: 2.5,
              py: 0.75,
              fontWeight: 700,
              fontSize: '0.8rem',
              textTransform: 'none',
              border: 'none',
              color: COLORS.textMuted,
              '&.Mui-selected': {
                background: COLORS.pinkGradient,
                color: 'white',
                boxShadow: '0 4px 15px rgba(236, 72, 153, 0.35)',
                '&:hover': {
                  background: COLORS.pinkGradient,
                },
              },
              '&:hover': {
                bgcolor: alpha(COLORS.primary, 0.08),
              },
            },
          }}
        >
          <ToggleButton value="D">Daily</ToggleButton>
          <ToggleButton value="W">Weekly</ToggleButton>
          <ToggleButton value="M">Monthly</ToggleButton>
          <ToggleButton value="Y">Yearly</ToggleButton>
        </ToggleButtonGroup>
        {selectedPeriod === 'Y' && (
          <Typography variant="caption" color={COLORS.textMuted} sx={{ ml: 1 }}>
            April - March (Financial Year)
          </Typography>
        )}
      </Box>

      {/* ===== OPEN POSITIONS PLACEHOLDER ===== */}
      <Card
        sx={{
          borderRadius: '24px',
          border: `1px solid ${COLORS.border}`,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
          mb: 4,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <SectionHeader
            icon={<ShowChartIcon />}
            title="Open Positions"
            subtitle={`${totalOpenTrades} active positions`}
            action={
              <Button
                size="small"
                onClick={() => navigate('/trades?status=open')}
                sx={{
                  color: COLORS.primary,
                  fontWeight: 600,
                  '&:hover': { bgcolor: alpha(COLORS.primary, 0.1) },
                }}
              >
                View All
              </Button>
            }
          />
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height={120}
            sx={{
              bgcolor: '#F8FAFC',
              borderRadius: '16px',
              border: '1px dashed #E2E8F0',
            }}
          >
            <Box textAlign="center">
              <ShowChartIcon sx={{ fontSize: 40, color: COLORS.textMuted, mb: 1 }} />
              <Typography color="text.secondary" fontWeight={500}>
                {totalOpenTrades > 0
                  ? `${totalOpenTrades} open positions across E: ${dashboardData?.openTradesByType?.equity || 0} | F: ${dashboardData?.openTradesByType?.future || 0} | O: ${dashboardData?.openTradesByType?.option || 0}`
                  : 'No open positions'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ===== URGENT CLOSURE SECTION ===== */}
      <Card
        sx={{
          borderRadius: '24px',
          border: `1px solid ${alpha(COLORS.negative, 0.2)}`,
          bgcolor: alpha(COLORS.negative, 0.02),
          boxShadow: `0 4px 20px ${alpha(COLORS.negative, 0.08)}`,
          overflow: 'hidden',
          mb: 4,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <SectionHeader
            icon={<GavelIcon />}
            title="Urgent Closure"
            subtitle="Trades furthest from stop loss requiring attention"
          />
          {/* We don't have individual trades in dashboardData, show a message or placeholder */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height={100}
            sx={{
              bgcolor: alpha(COLORS.negative, 0.03),
              borderRadius: '16px',
              border: `1px dashed ${alpha(COLORS.negative, 0.15)}`,
            }}
          >
            <Box textAlign="center">
              <WarningAmberIcon sx={{ fontSize: 32, color: COLORS.textMuted, mb: 0.5 }} />
              <Typography color="text.secondary" fontWeight={500} variant="body2">
                No urgent closures
              </Typography>
              <Typography color={COLORS.textMuted} variant="caption">
                Trades nearing stop loss will appear here
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ===== CLIENT SUMMARY TABLE (Full Width) ===== */}
      <Card
        sx={{
          borderRadius: '24px',
          border: `1px solid ${COLORS.border}`,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
          mb: 4,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <SectionHeader
            icon={<TableChartIcon />}
            title="Client Breakdown"
            subtitle="Detailed client performance overview"
            action={
              <Button
                size="small"
                onClick={() => navigate('/clients')}
                sx={{
                  color: COLORS.primary,
                  fontWeight: 600,
                  '&:hover': { bgcolor: alpha(COLORS.primary, 0.1) },
                }}
              >
                View All
              </Button>
            }
          />
          <ClientSummaryTable data={dashboardData?.clientSummaries || []} loading={loading} />
        </CardContent>
      </Card>

      {/* ===== DEALER REVENUE POPUP ===== */}
      <DealerRevenuePopup
        open={dealerRevenueOpen}
        onClose={() => setDealerRevenueOpen(false)}
        clientSummaries={dashboardData?.clientSummaries || []}
      />

      {/* ===== SNACKBARS ===== */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: '12px' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ borderRadius: '12px' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* ===== IMPORT DIALOG ===== */}
      <Dialog
        open={importDialogOpen}
        onClose={() => !importing && setImportDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '24px' },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '12px',
                  bgcolor: alpha(COLORS.primary, 0.1),
                  display: 'flex',
                }}
              >
                <CloudUploadIcon sx={{ color: COLORS.primary }} />
              </Box>
              <Typography variant="h5" fontWeight={700}>
                Import Trades
              </Typography>
            </Box>
            <IconButton onClick={() => setImportDialogOpen(false)} disabled={importing}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {importing && <LinearProgress sx={{ mb: 2, borderRadius: '4px' }} color="success" />}

          {/* Upload Area */}
          <Box
            sx={{
              border: `2px dashed ${COLORS.border}`,
              borderRadius: '16px',
              p: 5,
              textAlign: 'center',
              bgcolor: '#FAFBFC',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: COLORS.primary,
                bgcolor: alpha(COLORS.primary, 0.05),
              },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <CloudUploadIcon sx={{ fontSize: 56, color: COLORS.primary, mb: 2 }} />
            <Typography variant="h6" fontWeight={600} color={COLORS.text}>
              Drop CSV file here or click to upload
            </Typography>
            <Typography variant="body2" color={COLORS.textMuted} mt={1}>
              Required columns: clientId, tradeType, date, scrip, purchaseLots, lotSize, purchasePrice
            </Typography>
          </Box>

          {/* Import Result */}
          {importResult && (
            <Alert
              severity={importResult.failed > 0 ? 'warning' : 'success'}
              sx={{ mt: 3, borderRadius: '14px' }}
              icon={importResult.failed > 0 ? <ErrorIcon /> : <CheckCircleIcon />}
            >
              <Typography variant="body2">
                <strong>{importResult.success}</strong> trades imported successfully
                {importResult.failed > 0 && `, ${importResult.failed} failed`}
              </Typography>
            </Alert>
          )}

          {/* Errors */}
          {importErrors.length > 0 && (
            <Alert severity="error" sx={{ mt: 3, borderRadius: '14px' }}>
              <Typography variant="body2" fontWeight={600} mb={1}>
                Import Errors:
              </Typography>
              {importErrors.slice(0, 5).map((err, i) => (
                <Typography key={i} variant="caption" display="block">
                  {err}
                </Typography>
              ))}
              {importErrors.length > 5 && (
                <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                  ...and {importErrors.length - 5} more errors
                </Typography>
              )}
            </Alert>
          )}

          {/* Preview Table */}
          {importData.length > 0 && (
            <Box mt={3}>
              <Typography variant="subtitle2" fontWeight={600} mb={2}>
                Preview ({importData.length} records)
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300, borderRadius: '14px' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#F8FAFC' }}>Client ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#F8FAFC' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#F8FAFC' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#F8FAFC' }}>Scrip</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#F8FAFC' }}>Lots</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, bgcolor: '#F8FAFC' }}>Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importData.slice(0, 10).map((row, index) => (
                      <TableRow key={index} sx={{ '&:hover': { bgcolor: alpha(COLORS.primary, 0.05) } }}>
                        <TableCell>{row.clientId}</TableCell>
                        <TableCell>{row.tradeType}</TableCell>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>
                          <Chip label={row.scrip} size="small" sx={{ fontWeight: 600 }} />
                        </TableCell>
                        <TableCell align="right">{row.purchaseLots}</TableCell>
                        <TableCell align="right">{row.purchasePrice}</TableCell>
                      </TableRow>
                    ))}
                    {importData.length > 10 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ color: COLORS.textMuted }}>
                          ...and {importData.length - 10} more rows
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Sample Format */}
          <Box mt={3} p={2.5} bgcolor="#F8FAFC" borderRadius="14px" border={`1px solid ${COLORS.border}`}>
            <Typography variant="subtitle2" fontWeight={600} mb={1} color={COLORS.text}>
              Sample CSV Format:
            </Typography>
            <Typography
              variant="caption"
              component="pre"
              sx={{ fontFamily: 'monospace', color: COLORS.textMuted, whiteSpace: 'pre-wrap', fontSize: '0.75rem' }}
            >
              clientId,tradeType,date,scrip,purchaseLots,lotSize,purchasePrice,saleLots,salePrice,currentPrice
              {'\n'}client-123,Stock,2024-01-15,RELIANCE,10,1,2500,5,2600,2550
              {'\n'}client-123,Option,2024-01-16,NIFTY,2,50,150,0,0,180
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setImportDialogOpen(false)}
            disabled={importing}
            sx={{ color: COLORS.textMuted, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={importing || importData.length === 0}
            sx={{
              background: COLORS.pinkGradient,
              px: 4,
              py: 1.5,
              borderRadius: '12px',
              fontWeight: 700,
              boxShadow: '0 8px 25px rgba(236, 72, 153, 0.35)',
              '&:hover': {
                boxShadow: '0 12px 35px rgba(236, 72, 153, 0.45)',
              },
              '&:disabled': {
                background: '#E2E8F0',
              },
            }}
          >
            {importing ? 'Importing...' : `Import ${importData.length} Trades`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Dashboard;
